import styled from 'styled-components';

/* ─── Root — transparent; fills the OBS browser source (1080×1920) ────────── */

export const ViewerRoot = styled.div`
    background: transparent;
    bottom: 0;
    display: flex;
    flex-direction: column;
    left: 0;
    overflow: hidden;
    position: fixed;
    right: 0;
    top: 0;
`;

/* ─── Main — vertical stack, centred ─────────────────────────────────────── */

export const Main = styled.div`
    align-items: center;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: clamp(24px, 3vh, 48px);
    justify-content: top;
    z-index: 2;
`;

/* ─── Card slot (top of main, centred) ───────────────────────────────────── */

export const CardSlot = styled.div`
    aspect-ratio: 2.5 / 3.5;
    background: ${({ $accent }) =>
        $accent ? `rgba(${$accent}, 0.18)` : 'rgba(255,255,255,0.10)'};
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1.5px solid rgba(255, 255, 255, 0.25);
    border-radius: clamp(10px, 2vw, 24px);
    box-shadow: 0 8px 48px rgba(0,0,0,0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
    position: relative;
    width: clamp(180px, 38vw, 420px);
`;

export const CardSlotInner = styled.div`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 12px;
    inset: 0;
    justify-content: center;
    position: absolute;
`;

export const CardSlotIcon = styled.div`
    color: rgba(255, 255, 255, 0.35);
    svg { height: clamp(40px, 8vw, 80px); width: clamp(40px, 8vw, 80px); }
`;

export const CardSlotLabel = styled.span`
    color: rgba(255, 255, 255, 0.28);
    font-family: 'Inter', sans-serif;
    font-size: clamp(10px, 1.4vw, 15px);
    letter-spacing: 0.12em;
    text-transform: uppercase;
`;

/* ─── Info panel (below card slot) ──────────────────────────────────────── */

export const InfoPanel = styled.div`
    align-items: center;
    background: rgba(0, 0, 0, 0.42);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: clamp(16px, 2.5vw, 32px);
    box-shadow: 0 12px 64px rgba(0,0,0,0.4);
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: clamp(28px, 5vw, 56px) clamp(24px, 5vw, 56px);
    text-align: center;
    width: 100%;
    padding-top: 100px;
`;

export const AttrRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: clamp(6px, 1vw, 12px);
    justify-content: center;
    margin-bottom: clamp(16px, 3vh, 36px);
`;

export const Attr = styled.span`
    background: ${({ $accent }) => $accent ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.14)'};
    border: 0.5px solid rgba(255, 255, 255, 0.28);
    border-radius: 99px;
    color: #fff;
    font-family: 'Inter', sans-serif;
    font-size: clamp(10px, 1.5vw, 16px);
    font-weight: 500;
    padding: 6px 18px;
    white-space: nowrap;
`;

export const DetailRow = styled.div`
    display: flex;
    gap: clamp(24px, 5vw, 56px);
    justify-content: center;
    width: 100%;
`;

export const DetailLabel = styled.div`
    color: rgba(255, 255, 255, 0.4);
    font-family: 'Inter', sans-serif;
    font-size: 32px;
    font-weight: 500;
    letter-spacing: 0.1em;
    margin-bottom: 4px;
    text-transform: uppercase;
`;

export const DetailVal = styled.div`
    color: ${({ $highlight }) => $highlight ? '#fff' : 'rgba(255,255,255,0.85)'};
    font-family: 'Inter', sans-serif;
    font-size: 48px;
    font-weight: ${({ $highlight }) => $highlight ? 700 : 500};
`;

/* ─── Price tag ──────────────────────────────────────────────────────────── */

export const PriceTag = styled.div`
    align-items: baseline;
    align-self: center;
    display: inline-flex;
    gap: 4px;
    margin-top: clamp(16px, 2.5vh, 28px);
`;

export const PriceCurrency = styled.span`
    color: rgba(255, 255, 255, 0.5);
    font-family: 'Inter', sans-serif;
    font-size: clamp(16px, 2.2vw, 26px);
    font-weight: 500;
`;

export const PriceValue = styled.span`
    color: #fff;
    font-family: 'Roboto Condensed', sans-serif;
    font-size: clamp(28px, 5vw, 60px);
    font-weight: 700;
    text-shadow: 0 2px 16px rgba(0,0,0,0.4);
`;

export const PriceLabel = styled.span`
    color: rgba(255, 255, 255, 0.4);
    font-family: 'Inter', sans-serif;
    font-size: clamp(11px, 1.2vw, 14px);
    letter-spacing: 0.06em;
    margin-left: 8px;
    text-transform: uppercase;
`;

/* ─── Progress strip ─────────────────────────────────────────────────────── */

export const ProgressStrip = styled.div`
    background: rgba(255, 255, 255, 0.14);
    border-radius: 99px;
    bottom: 0;
    height: 8px;
    left: 0;
    margin: 0 48px 40px;
    position: absolute;
    right: 0;
    z-index: 3;
`;

export const ProgressFill = styled.div`
    background: rgba(255, 255, 255, 0.6);
    border-radius: 99px;
    height: 100%;
    transition: width 0.6s ease;
    width: ${({ $pct }) => $pct ?? 0}%;
`;

/* ─── Waiting state ──────────────────────────────────────────────────────── */

export const WaitingOverlay = styled.div`
    align-items: center;
    background: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 24px;
    color: rgba(255, 255, 255, 0.35);
    display: flex;
    flex-direction: column;
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    gap: 10px;
    justify-content: center;
    margin: auto;
    padding: 48px 64px;
    text-align: center;

    p { color: rgba(255,255,255,0.18); font-size: 13px; margin: 0; }
`;
